import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { PersonUtils } from 'src/app/auth_profile/utils/v3/person-utils';
import { LessonType } from 'src/app/col/model/calendar';
import * as LessonsV3 from 'src/app/col/model/v3/lessons';
import { NativeServiceApiProvider } from 'src/app/services/native-api-provider.service';
import { LessonTypeUtilsV3 } from 'src/app/col/utils/lesson-utils-v3';
import { Pageable, Page } from 'src/app/utils/pageable';
import { SpinnerService } from 'src/app/utils/services/spinner.service';

@Component({
    selector: 'app-teacher-lessons',
    templateUrl: './teacher-lessons.component.html',
    styleUrls: ['./teacher-lessons.component.scss'],
    standalone: false
})
export class TeacherLessonsComponent implements OnInit {
  private _lessonType: LessonType;
  private _teacherId: number;
  isLoading = true;
  pageable: Pageable;
  lessons: Page<LessonsV3.LFTeacherPreview>;
  langCode: string;

  @Input()
  set teacherId(value: number) {
    this._teacherId = value;
    this.getLessons(this.pageable).subscribe();
  }
  get teacherId() {
    return this._teacherId;
  }

  @Input()
  public set lessonType(lessonType: LessonType) {
    this._lessonType = lessonType;
    if (this._lessonType === LessonType.Past) {
      this.pageable = new Pageable(0, 5, [
        'metric.details.plannedStartDate,DESC',
      ]);
    } else {
      this.pageable = new Pageable(0, 5, [
        'metric.details.plannedStartDate,ASC',
      ]);
    }
    this.getLessons(this.pageable).subscribe();
  }
  get lessonType() {
    return this._lessonType;
  }

  constructor(
    private spinner: SpinnerService,
    private provider: NativeServiceApiProvider,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {}

  getLessonTypeName() {
    switch (this._lessonType) {
      case LessonType.Upcoming: {
        return 'COL.LESSONS.LIST.FUTURE_LESSONS';
      }
      case LessonType.Past: {
        return 'COL.LESSONS.LIST.PAST_LESSONS';
      }
      case LessonType.Incomplete: {
        return 'COL.LESSONS.LIST.INCOMPLETE_LESSONS';
      }
    }
  }

  onPageChange($event: any) {
    this.getLessons($event).subscribe();
  }

  getLessons(pageable: Pageable): Observable<Page<LessonsV3.LFTeacherPreview>> {
    if (
      this.lessonType === null ||
      this.lessonType === undefined ||
      !this.teacherId
    )
      return of(null);
    this.pageable = pageable;
    const { statuses, dateFrom, dateTo } = this.getLessonFilters();
    return this.spinner.trace<Page<LessonsV3.LFTeacherPreview>>(
      this.provider
        .colV3()
        .pipe(
          switchMap((api) =>
            api.findLessons(
              LessonsV3.LessonCategory.Col,
              null,
              'teacher-preview',
              null,
              null,
              this.teacherId,
              null,
              null,
              this.langCode,
              statuses,
              dateFrom,
              dateTo,
              this.pageable
            )
          ),
          map((lessonsPage) => this.mapLessonsPageFromV3(lessonsPage))
        )
        .pipe(
          tap((lessons) => (this.lessons = lessons)),
          tap((_) => (this.isLoading = false))
        )
    );
  }

  // Every lesson lands in exactly one card: now splits Upcoming from the other two, then
  // the status decides between Incomplete and Past.
  //
  //   Upcoming   | all eight statuses                                  | planned start >= now
  //   Incomplete | Initializing, Booked, Due, InProgress                | planned start <= now
  //   Past       | Complete, Cancel, InvalidatedByTeacher/ByStudent     | planned start <= now
  private getLessonFilters(): {
    statuses: LessonsV3.LessonStatus[];
    dateFrom: Date | null;
    dateTo: Date | null;
  } {
    const now = new Date();
    switch (this.lessonType) {
      case LessonType.Incomplete:
        return {
          statuses: [
            LessonsV3.LessonStatus.Initializing,
            LessonsV3.LessonStatus.Booked,
            LessonsV3.LessonStatus.Due,
            LessonsV3.LessonStatus.InProgress,
          ],
          dateFrom: null,
          dateTo: now,
        };
      case LessonType.Past:
        return {
          statuses: [
            LessonsV3.LessonStatus.Complete,
            LessonsV3.LessonStatus.Cancel,
            LessonsV3.LessonStatus.InvalidatedByStudent,
            LessonsV3.LessonStatus.InvalidatedByTeacher,
          ],
          dateFrom: null,
          dateTo: now,
        };
      case LessonType.Upcoming:
      default:
        return {
          statuses: [
            LessonsV3.LessonStatus.Initializing,
            LessonsV3.LessonStatus.Booked,
            LessonsV3.LessonStatus.Due,
            LessonsV3.LessonStatus.InProgress,
            LessonsV3.LessonStatus.Complete,
            LessonsV3.LessonStatus.Cancel,
            LessonsV3.LessonStatus.InvalidatedByStudent,
            LessonsV3.LessonStatus.InvalidatedByTeacher,
          ],
          dateFrom: now,
          dateTo: null,
        };
    }
  }

  private mapLessonsPageFromV3(
    lessonsPage: Page<LessonsV3.LFViewBase>
  ): Page<LessonsV3.LFTeacherPreview> {
    return {
      ...lessonsPage,
      content: (lessonsPage?.content ?? []).map((lesson) =>
        this.mapLessonFromV3(lesson)
      ),
    } as Page<LessonsV3.LFTeacherPreview>;
  }

  private mapLessonFromV3(
    lesson: LessonsV3.LFViewBase
  ): LessonsV3.LFTeacherPreview {
    const source = lesson as any;
    const metric = source.lessonMetric ?? {};
    source.lessonMetric = {
      ...metric,
      plannedStartDate: metric.plannedStartDate
        ? new Date(metric.plannedStartDate)
        : null,
      started: metric.started ? new Date(metric.started) : null,
    };
    return source as LessonsV3.LFTeacherPreview;
  }

  public hasResults(): boolean {
    return !!this.lessons;
  }

  public getTotalResults(): number {
    if (!this.lessons) return 0;
    return this.lessons.totalElements;
  }

  getLessonsToDisplay() {
    return this.lessons.content;
  }

  getInitials(person: LessonsV3.PersonDetailedRef) {
    return person ? PersonUtils.getInitials(person) : '';
  }

  public getLessonStudentPhotoUrl(_lesson: LessonsV3.LFTeacherPreview): string {
    return null;
  }

  public lessonBadgeClass(lesson: LessonsV3.LFTeacherPreview) {
    return lesson ? LessonTypeUtilsV3.getStudentLessonColorClass(lesson.status) : '';
  }

  getLessonStatus(lesson: LessonsV3.LFTeacherPreview) {
    return LessonTypeUtilsV3.getStudentLessonStatus(lesson.status);
  }

  public getLessonDate(lesson: LessonsV3.LFTeacherPreview): Date {
    return lesson.lessonMetric.plannedStartDate;
  }

  public getLessonStudent(lesson: LessonsV3.LFTeacherPreview): string {
    return lesson.students
      .map((s) => s.reference?.person)
      .filter((p) => !!p)
      .map((p) => PersonUtils.getPersonName(p))
      .join(', ');
  }

  openDetails(lesson: LessonsV3.LFTeacherPreview) {
    const studentId = lesson.students?.[0]?.reference?.id;
    if (!studentId) {
      return;
    }
    this.router.navigate(
      [`lesson/${lesson.id}/student/${studentId}`],
      {
        relativeTo: this.route,
      }
    ).then();
  }


  buildVersionNumber(courseCode: string, productVersion: string) {
    if (!courseCode.startsWith("en.s") && !productVersion) return null;
    if (!productVersion) return '2nd Ed.';
    const intVer = parseInt(productVersion);
    if (isNaN(intVer)) return `v: ${productVersion}`;
    const lastDigit = intVer % 10;
    let postfix: string;
    if (intVer > 3 && intVer < 20) postfix = 'th';
    else if (lastDigit == 1) postfix = 'st';
    else if (lastDigit == 2) postfix = 'nd';
    else if (lastDigit == 3) postfix = 'rd';
    else postfix = 'th';
    return `${intVer}${postfix} Ed.`;
  }
  getProductVersion(lesson: LessonsV3.LFTeacherPreview) {
    return this.buildVersionNumber(lesson.course.code, lesson.productVersion);
  }


  isRedLabel(lesson: LessonsV3.LFTeacherPreview) {
    return lesson.course.code.startsWith('en.s') && lesson.productVersion == null
  }
}
